/**
 * Destructive-action confirmation for the creator studios.
 *
 * Deleting a block, a page or a capsule slide throws away content that is never
 * auto-saved, so it asks first — and names what is about to be lost, because
 * "are you sure?" on its own gives the creator nothing to decide with.
 *
 * Shared rather than copied: both studios ask the same question in the same
 * words, and the app's own dialog chrome answers Settings → Appearance without
 * either studio owning a colour.
 */
import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {Text} from '#/components/Typography'

export function StudioConfirmDialog({
  control,
  title,
  message,
  confirmLabel,
  onConfirm,
}: {
  control: Dialog.DialogControlProps
  /** The question, e.g. "Delete this block?". */
  title: string
  /** What exactly is lost if they confirm. */
  message: string
  confirmLabel: string
  onConfirm: () => void
}) {
  return (
    <Dialog.Outer
      control={control}
      nativeOptions={{preventExpansion: true}}
      webOptions={{alignCenter: true}}>
      <Dialog.Handle />
      <DialogInner
        title={title}
        message={message}
        confirmLabel={confirmLabel}
        onConfirm={onConfirm}
      />
    </Dialog.Outer>
  )
}

function DialogInner({
  title,
  message,
  confirmLabel,
  onConfirm,
}: {
  title: string
  message: string
  confirmLabel: string
  onConfirm: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const control = Dialog.useDialogContext()

  return (
    <Dialog.ScrollableInner style={web({maxWidth: 420})} label={title}>
      <View style={[a.gap_2xl]}>
        <View style={[a.gap_sm]}>
          <Text style={[a.font_bold, a.text_2xl]}>{title}</Text>
          <Text
            style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}>
            {message}
          </Text>
        </View>

        <View style={[a.gap_sm]}>
          <Button
            label={confirmLabel}
            accessibilityHint={message}
            onPress={() => {
              // Close first, then act: the dialog must not outlive the thing it
              // was asking about (a deleted page would leave it dangling).
              control.close(() => onConfirm())
            }}
            size="large"
            variant="solid"
            color="negative_subtle"
            testID="studioConfirmDeleteBtn">
            <ButtonText>{confirmLabel}</ButtonText>
          </Button>
          <Button
            label={l`Cancel`}
            accessibilityHint={l`Returns to the editor unchanged`}
            onPress={() => control.close()}
            size="large"
            variant="ghost"
            color="secondary">
            <ButtonText>
              <Trans>Cancel</Trans>
            </ButtonText>
          </Button>
        </View>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}
